import moment from 'moment';
import TextField from '@mui/material/TextField';
import DataTable from 'react-data-table-component';
import { useDispatch } from 'react-redux';
import { Select, MenuItem, Checkbox } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap';

import './index.css';
import TitleRoute from '../../components/titleRoute.js';
import { getWorkspaceActivities, deleteActivity } from '../../store/Activities';
import TableAnimationFiles from '../../components/Animations/TableAnimationFiles.js';
import notFoundImg from '../../static/img/not-found.png';

// Activities page — workspace-wide notes, emails, calls and SMS, same UI shell as Call Logs.
// The sidebar submenu (Notes/Emails/Calls/SMS) drives the active type via the path.
const Activities = () => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();

    const activeTab = tabFromPathname(pathname);
    const [ui, setUi] = useState(INITIAL_UI);
    const updateUi = (patch) => setUi(prev => ({ ...prev, ...patch }));

    const fetchActivities = useCallback((params) => {
        updateUi({ loading: true });
        dispatch(getWorkspaceActivities(
            params,
            (body) => updateUi({ loading: false, rows: body['activities'] || [], total: body['pagination']?.['totalItems'] || 0 }),
            () => updateUi({ loading: false })
        ));
    }, [dispatch]);

    // Debounce the quick search so we don't hit the API per keystroke
    useEffect(() => {
        const timer = setTimeout(() => setUi(prev => ({ ...prev, search: prev['searchInput'].trim(), page: 1 })), 400);
        return () => clearTimeout(timer);
    }, [ui['searchInput']]);

    useEffect(() => {
        fetchActivities({ type: activeTab['type'], page: ui['page'], limit: ui['limit'], search: ui['search'], status: ui['status'] });
    }, [activeTab['type'], ui['page'], ui['limit'], ui['search'], ui['status'], fetchActivities]);

    // Back to page 1 and clear selection when switching type via the sidebar submenu
    useEffect(() => { setUi(prev => ({ ...prev, page: 1, selectedRows: [], toggleCleared: !prev['toggleCleared'] })); }, [activeTab['type']]);

    const handleStatusFilter = (status) => updateUi({ status: ui['status'] === status ? '' : status, page: 1 });

    const handleRefresh = () => {
        if (ui['refreshing']) return;
        updateUi({ refreshing: true });
        fetchActivities({ type: activeTab['type'], page: ui['page'], limit: ui['limit'], search: ui['search'], status: ui['status'] });
        setTimeout(() => updateUi({ refreshing: false }), 1500);
    };

    const clearSelection = () => updateUi({ selectedRows: [], toggleCleared: !ui['toggleCleared'] });

    // Reason: the activities API only exposes single-record delete — dispatch one delete per row,
    // then refetch once the batch has had time to land
    const confirmBulkDelete = () => {
        for (const row of ui['selectedRows']) dispatch(deleteActivity(row['_id']));
        updateUi({ showDeleteConfirm: false });
        clearSelection();
        setTimeout(handleRefresh, 900);
    };

    return (
        <div className='content activities-content'>
            <div className='allemails-area'>
                <div className='row'>
                    <div className='col-lg-4 col-md-6 col-sm-6'>
                        <div className='title-top-block'>
                            <div className='left-area'>
                                <TitleRoute title={activeTab['label']} />
                                {!ui['loading'] && <p className='records-title'>{ui['total']} records</p>}
                            </div>
                        </div>
                    </div>
                    <div className='col-12'>
                        <div className='table-container filters-table calllogs-table'>
                            {ui['selectedRows'].length > 0 ? (
                                <div className='header-table-filters advanced-table-filters style-filter-show'>
                                    <div className='left-area'>
                                        <p>{ui['selectedRows'].length} {activeTab['label'].toLowerCase()} selected</p>
                                        <button className='assing-btn assing-btn-delete' onClick={() => updateUi({ showDeleteConfirm: true })}>
                                            <i className='icon fa fa-trash-o'></i>
                                            Delete
                                        </button>
                                    </div>
                                    <div className='right-side-filter'>
                                        <button className='close' onClick={clearSelection}>
                                            <i className='fa fa-times' />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className='header-table-filters advanced-table-filters'>
                                    <div className='left-area'>
                                        <div className='searchbox'>
                                            <TextField fullWidth hiddenLabel size='small' name='search' variant='standard' placeholder={`Search ${activeTab['label'].toLowerCase()}...`} value={ui['searchInput']} onChange={e => updateUi({ searchInput: e['target']['value'] })} />
                                            <i className='icon'>
                                                <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                                    <path d='M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z' fill='#505258' stroke='#505258' strokeWidth='0.236842' />
                                                    <path d='M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z' fill='#505258' stroke='#505258' strokeWidth='0.236842' />
                                                </svg>
                                            </i>
                                        </div>
                                    </div>
                                    <div className='right-side-filter'>
                                        <div className='filters-dates'>
                                            <Select displayEmpty value='' renderValue={() => ui['status'] ? `Status: ${ui['status'].replace('-', ' ')}` : 'Status'}>
                                                {STATUS_OPTIONS.map((status) => (
                                                    <MenuItem key={status} value={status} onClick={() => handleStatusFilter(status)}>
                                                        <Checkbox checked={ui['status'] === status} />
                                                        {status.toUpperCase().replace('-', ' ')}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </div>
                                        <button className='ml-2 calllogs-refresh-btn' onClick={handleRefresh} title='Refresh'>
                                            <i className={`fa fa-sync-alt${ui['refreshing'] ? ' fa-spin' : ''}`} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <style>{checkboxStyles}</style>
                            <DataTable
                                columns={ACTIVITY_COLUMNS}
                                data={ui['rows']}
                                fixedHeader
                                selectableRows
                                persistTableHead
                                progressPending={ui['loading']}
                                progressComponent={<div className='activities-loader-box'><TableAnimationFiles /></div>}
                                pagination
                                paginationServer
                                paginationTotalRows={ui['total']}
                                paginationPerPage={ui['limit']}
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                onChangePage={(page) => updateUi({ page })}
                                onChangeRowsPerPage={(limit, page) => updateUi({ limit, page })}
                                clearSelectedRows={ui['toggleCleared']}
                                onSelectedRowsChange={({ selectedRows }) => updateUi({ selectedRows })}
                                customStyles={customStyles}
                                className='table-sellagent datatable--paginated'
                                paginationComponentOptions={{ rangeSeparatorText: 'of', rowsPerPageText: 'per page:' }}
                                noDataComponent={<div className='notfound-area'>
                                    <div className='img-box'>
                                        <img alt='...' src={notFoundImg} />
                                    </div>
                                    <p>No {activeTab['label'].toLowerCase()} found</p>
                                </div>}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Modal isOpen={ui['showDeleteConfirm']} toggle={() => updateUi({ showDeleteConfirm: false })} className='main-modal modal-dialog-centered'>
                <ModalHeader toggle={() => updateUi({ showDeleteConfirm: false })}><h3>Delete {activeTab['label']}</h3></ModalHeader>
                <ModalBody>
                    <div className='modal-area'>
                        <p>Are you sure you want to delete {ui['selectedRows'].length} {activeTab['label'].toLowerCase()}? This cannot be undone.</p>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button className='btn-style-border' onClick={() => updateUi({ showDeleteConfirm: false })}>Cancel</button>
                    <button className='btn-style-new' onClick={confirmBulkDelete}>Delete</button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

const ACTIVITY_TABS = [
    { type: 'note', slug: 'notes', label: 'Notes' },
    { type: 'email', slug: 'emails', label: 'Emails' },
    { type: 'call', slug: 'calls', label: 'Calls' },
    { type: 'sms', slug: 'sms', label: 'SMS' }
];

// Each type is its own top-level route (/notes, /emails, /calls, /sms) — see routes.js

const STATUS_OPTIONS = ['pending', 'in-progress', 'completed', 'cancelled', 'failed'];

const INITIAL_UI = { page: 1, limit: 50, rows: [], total: 0, loading: false, refreshing: false, searchInput: '', search: '', status: '', selectedRows: [], toggleCleared: false, showDeleteConfirm: false };

const STATUS_TONES = { completed: 'completed', pending: 'pending', 'in-progress': 'pending', cancelled: 'cancelled', failed: 'cancelled' };

function tabFromPathname(pathname) {
    return ACTIVITY_TABS.find(t => pathname.endsWith(`/${t['slug']}`)) || ACTIVITY_TABS[0];
}

const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const activityDetails = (row) => {
    const text = stripHtml(row['metadata']?.['subject'] || row['description'] || row['content'] || row['metadata']?.['body']);
    return text.length > 120 ? `${text.slice(0, 120)}…` : text || '—';
};

const personName = (person) => person ? `${person['firstName'] || ''} ${person['lastName'] || ''}`.trim() || person['email'] || '—' : '—';

// Parent-object relation types shown in the Related To column, in display order
const RELATED_TYPES = [
    { key: 'contacts', label: 'Contact', icon: 'fa-user' },
    { key: 'companies', label: 'Company', icon: 'fa-building' },
    { key: 'deals', label: 'Deal', icon: 'fa-briefcase' }
];

const relatedObjectPills = (row) => {
    const related = row['relatedObjects'] || {};
    const pills = RELATED_TYPES.flatMap(({ key, label, icon }) => (related[key] || []).map(obj => ({ ...obj, key, label, icon })));
    if (pills.length === 0) return <span className='activities-related-empty'>—</span>;
    return (
        <div className='activities-related-cell'>
            {pills.slice(0, 3).map(pill => (
                <Link key={`${pill['key']}-${pill['_id']}`} className='activities-related-pill' to={`/${pill['key']}/${pill['_id']}`} title={`${pill['label']}: ${pill['name']}`}>
                    <span className='activities-related-pill-type'><i className={`fa ${pill['icon']}`} /> {pill['label']}</span>
                    <span className='activities-related-pill-name'>{pill['name']}</span>
                </Link>
            ))}
            {pills.length > 3 && <span className='activities-related-more'>+{pills.length - 3}</span>}
        </div>
    );
};

const ACTIVITY_COLUMNS = [
    { name: 'Time', selector: row => row['createdAt'], sortable: true, width: '180px', format: row => row['createdAt'] ? moment(row['createdAt']).format('lll') : '—' },
    { name: 'Title', selector: row => row['title'], sortable: true, grow: 1.4, cell: row => <span className='activities-table-title'>{row['title'] || '—'}</span> },
    { name: 'Details', selector: row => activityDetails(row), grow: 2, wrap: true, cell: row => <span className='activities-table-details'>{activityDetails(row)}</span> },
    { name: 'Related To', grow: 1.4, cell: row => relatedObjectPills(row) },
    { name: 'Status', selector: row => row['status'], sortable: true, width: '150px', cell: row => <button className={`call-status-btn activities-status-${STATUS_TONES[row['status']] || 'pending'}`}>{(row['status'] || 'pending').toUpperCase().replace('-', ' ')}</button> },
    { name: 'Owner', selector: row => personName(row['createdBy']), width: '170px' }
];

const checkboxStyles = `
  .table-sellagent input[type="checkbox"] {
    width: 13px;
    height: 13px;
    cursor: pointer;
    accent-color: #cbd6e2;
    transform: scale(1);
    opacity: 0.45;
  }

  .table-sellagent input[type="checkbox"]:checked {
    opacity: 1;
    accent-color: var(--primary-color);
  }
`;

const customStyles = {
    table: {
        style: {
            tableLayout: 'auto',
            minWidth: '1000px',
        },
    },
    cells: {
        style: {
            whiteSpace: 'normal',
        },
    },
};

export default Activities;
